<template>
  <vl-grid>
    <vl-column v-if="canAddStandardTest && !isReadOnly" width="12">
      <vl-action-group mod-align-right>
        <vl-button icon="plus" mod-icon-before @click="showAddGeckoTestModal">{{ $t('general.add') }}</vl-button>
      </vl-action-group>
    </vl-column>
    <vl-column v-if="data.loading && data.standardTests.length === 0" width="12" width-xs="12">
      <vl-region>
        <div v-vl-align:center>
          <vl-loader :message="$t('deliveryprocessdefinition.geckoSync.loadingStandardTests')" />
        </div>
      </vl-region>
    </vl-column>
    <vl-column v-else-if="data.standardTests.length > 0" width="12">
      <GeckoTestDatatable
        :items="data.standardTests"
        :loading="data.loading"
        :deliveryQualityCheckTypeId="1"
        :isReadOnly="isReadOnly"
        :onCheckboxChange="onCheckboxChanged"
        :onRemoveClick="showDeleteGeckoTestModal"
        :onInfoClick="showInfoGeckoTestModal"
        :onEditClick="showEditGeckoTestModal"
        :canEditItem="canEditGeckoTest"
      ></GeckoTestDatatable>
    </vl-column>
    <vl-column v-else-if="!canAddStandardTest" width="12">
      <span>{{ $t('deliveryprocessdefinition.geckoSync.noStandardTestsAvailable') }}</span>
    </vl-column>
    <vl-column v-else-if="data.standardTests.length === 0" width="12">
      <span>{{ $t('deliveryprocessdefinition.geckoSync.noStandardTestsForEntity') }}</span>
    </vl-column>
  </vl-grid>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { computed, reactive, watch } from 'vue';
import {
  DeliveryEntityDetailDto,
  GeckoControlType,
  GeckoStandardTestTypeDto,
  GeckoTestDto
} from '@/api/backoffice/clients';
import GeckoTestDatatable from '../../subcomponents/gecko-test-datatable/GeckoTestDatatable.vue';
import GeckoStandardTestForm, { GeckoStandardTestFormDto, TestTypeDto } from './GeckoStandardTestForm.vue';
import { useModalStore } from '@/modules/core/store/modal.store';
import { DeliveryQualityCheckData } from '../gecko-test-datatable/GeckoTestActiveToggle.vue';

const { t } = useI18n();
const modalStore = useModalStore();

const props = defineProps<{
  standardTests: GeckoTestDto[];
  standardTestTypes: GeckoStandardTestTypeDto[];
  deliveryEntity: DeliveryEntityDetailDto;
  isReadOnly: boolean;
  onCheckboxChange?: (evt: DeliveryQualityCheckData) => Promise<boolean>;
  onFormSubmit?: (dto: GeckoStandardTestFormDto) => Promise<boolean>;
  onDeleteTest?: (test: GeckoTestDto) => Promise<boolean>;
  onAfterChange?: () => Promise<void>;
}>();

const data = reactive({
  standardTests: [] as GeckoTestDto[],
  loading: false
});

watch(
  () => props.standardTests,
  () => {
    data.standardTests =
      props.standardTests
        ?.filter((test) => test.deliveryEntityId == props.deliveryEntity.id)
        .map((test) => ({ ...test })) || [];
  },
  { immediate: true }
);

const entityTypeName = computed(() => {
  return props.deliveryEntity?.model.entityTypeName;
});

const isTestAllowed = (testType: GeckoStandardTestTypeDto) => {
  if (!entityTypeName.value) {
    return false;
  }
  return testType.entityTypes.includes(entityTypeName.value);
};

const allowedTestTypes = computed((): TestTypeDto[] => {
  return props.standardTestTypes.filter(isTestAllowed).map((x) => ({
    id: x.id,
    name: x.name
  }));
});

const canAddStandardTest = computed(() => {
  return allowedTestTypes.value.length > 0;
});

const onCheckboxChanged = async (deliveryQualityCheckData: DeliveryQualityCheckData): Promise<boolean> => {
  return await props.onCheckboxChange?.(deliveryQualityCheckData);
};

const triggerAfterChange = (success: boolean): boolean => {
  if (success) {
    data.loading = true;
    props.onAfterChange?.().finally(() => {
      data.loading = false;
    });
  }
  return success;
};

const canEditGeckoTest = (test: GeckoTestDto): boolean => {
  // only allow editing if the test has parameters (since that's the only editable property in the form)
  const id = parseInt(test.test.code?.split('_')[0], 10);
  const standardTestType = props.standardTestTypes.find((x) => x.id === id);
  return standardTestType?.parameters?.length > 0 || false;
};

const showAddGeckoTestModal = () => {
  const title = t('deliveryprocessdefinition.geckoSync.actions.addStandardTest.label');

  const modalContent = {
    title,
    component: GeckoStandardTestForm,
    props: {
      standardTestTypes: props.standardTestTypes,
      entity: props.deliveryEntity,
      onFormSubmit: async (dto) => {
        const success = await props.onFormSubmit?.(dto);
        return triggerAfterChange(success);
      }
    },
    modLarge: true
  };

  modalStore.showModal(modalContent);
};

const showInfoGeckoTestModal = (geckoTest: GeckoTestDto) => {
  const title = t('deliveryprocessdefinition.geckoSync.actions.infoStandardTest.label');

  const modalContent = {
    title,
    component: GeckoStandardTestForm,
    props: {
      standardTestTypes: props.standardTestTypes,
      entity: props.deliveryEntity,
      testToEdit: geckoTest,
      readonly: true
    },
    modLarge: true
  };

  modalStore.showModal(modalContent);
};

const showEditGeckoTestModal = (geckoTest: GeckoTestDto) => {
  const title = t('deliveryprocessdefinition.geckoSync.actions.editStandardTest.label');

  const modalContent = {
    title,
    component: GeckoStandardTestForm,
    props: {
      standardTestTypes: props.standardTestTypes,
      entity: props.deliveryEntity,
      testToEdit: geckoTest,
      onFormSubmit: async (dto) => {
        const success = await props.onFormSubmit?.(dto);
        return triggerAfterChange(success);
      }
    },
    modLarge: true
  };

  modalStore.showModal(modalContent);
};

const showDeleteGeckoTestModal = (geckoTest: GeckoTestDto) => {
  const title = t('deliveryprocessdefinition.geckoSync.actions.deleteStandardTest.label');
  const okLabel = t('general.delete');
  const confirmationMessage = t('deliveryprocessdefinition.geckoSync.actions.deleteStandardTest.confirmationMessage', {
    code: geckoTest.test.code,
    entiteit: `${props.deliveryEntity.code} - ${props.deliveryEntity.label}`
  });

  modalStore.showConfirmModal({
    title,
    props: {
      confirmationMessage,
      okLabel,
      modError: true,
      confirm: async () => {
        const success = await props.onDeleteTest?.(geckoTest);
        return triggerAfterChange(success);
      }
    }
  });
};
</script>

<style lang="scss" scoped></style>
